import React, { useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { trackFormSubmission, trackQuoteRequest } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    service_type: '',
    message: '',
    location: '',
    preferred_contact: 'phone'
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      setStatus({ type: 'success', message: response.data.message });
      
      // Track conversion
      trackFormSubmission('Contact Form', {
        service_type: formData.service_type,
        location: formData.location
      });
      trackQuoteRequest(formData.service_type || 'General', formData.location);
      
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        service_type: '',
        message: '',
        location: '',
        preferred_contact: 'phone'
      });
    } catch (error) {
      // Surface the backend's actual validation message so users know what to fix.
      let detail = error?.response?.data?.detail;
      let msg = 'Error submitting form. Please try again or call us directly at 646-209-9955.';
      if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI/Pydantic validation errors: pick the most helpful one
        const first = detail[0];
        const field = (first.loc || []).slice(1).join('.') || 'field';
        msg = `Please fix the ${field}: ${first.msg}`;
      } else if (typeof detail === 'string' && detail.length > 0) {
        msg = detail;
      }
      setStatus({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO 
        title="Contact Us - Reclaim Exterminating"
        description="Contact Reclaim Exterminating for professional pest control services in NYC and Long Island. 24/7 emergency service available. Call (646) 209-9955 for a free quote."
        keywords="pest control contact, exterminator NYC, pest control Long Island, emergency pest service, free pest control quote"
      />
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl">Get in touch for fast, professional pest control service</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
              <p className="text-gray-700 mb-8">Have a pest problem? Contact us today for fast, effective solutions. We&apos;re available 24/7 for emergency services.</p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="text-3xl mr-4">📞</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Phone</h3>
                    <a href="tel:6462099955" className="text-blue-600 text-xl font-semibold hover:underline">(646) 209-9955</a>
                    <p className="text-sm text-gray-600">24/7 Emergency Service</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="text-3xl mr-4">✉️</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email</h3>
                    <a href="mailto:reclaimexterminating@gmail.com" className="text-blue-600 hover:underline">reclaimexterminating@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="text-3xl mr-4">🕒</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Hours</h3>
                    <p className="text-gray-700">24/7 Emergency Services Available</p>
                    <p className="text-sm text-gray-600">Regular hours: Mon-Sun, 8AM-8PM</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="text-3xl mr-4">📍</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Service Areas</h3>
                    <p className="text-gray-700">Queens, Manhattan, Brooklyn, Bronx, Staten Island, Nassau County, Suffolk County, Westchester, and all of Long Island</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-green-50 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">⚡ Emergency Service</h3>
                <p className="text-gray-700 mb-4">Need immediate help? Call us now for same-day service!</p>
                <a href="tel:6462099955" className="block bg-green-600 text-white text-center py-3 rounded-lg font-bold hover:bg-green-700 transition">Call Now</a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Request Free Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name *</label>
                    <input 
                      type="text" 
                      name="first_name" 
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name *</label>
                    <input 
                      type="text" 
                      name="last_name" 
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Service Type</label>
                  <select 
                    name="service_type" 
                    value={formData.service_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Select a service</option>
                    <option value="Rodent Control">Rodent Control</option>
                    <option value="Bed Bug Removal">Bed Bug Removal</option>
                    <option value="Cockroach Extermination">Cockroach Extermination</option>
                    <option value="Termite Control">Termite Control</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Location</label>
                  <input 
                    type="text" 
                    name="location" 
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., Queens, NY"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <textarea 
                    name="message" 
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="Tell us about your pest problem..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Preferred Contact Method</label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="preferred_contact" 
                        value="phone"
                        checked={formData.preferred_contact === 'phone'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Phone
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="preferred_contact" 
                        value="email"
                        checked={formData.preferred_contact === 'email'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Email
                    </label>
                  </div>
                </div>

                {status.message && (
                  <div className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {status.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:bg-gray-400"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Our Service Area</h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            We provide professional pest control services throughout NYC and Long Island, including Queens, Manhattan, Brooklyn, Bronx, Staten Island, Nassau County, Suffolk County, and Westchester.
          </p>
          <div className="max-w-4xl mx-auto">
            <iframe
              title="Reclaim Pest Control Service Area Map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-74.2591,40.4774,-73.4219,40.9176&layer=mapnik&marker=40.7282,-73.7949"
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              className="shadow-lg"
            />
            <div className="bg-white p-4 rounded-b-lg shadow-lg -mt-1 flex flex-wrap justify-center gap-4 text-sm">
              <a 
                href="https://www.google.com/maps/search/pest+control+queens+ny" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                View on Google Maps
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="https://www.google.com/maps/dir//Queens,+NY" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd"/></svg>
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;